import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { message, Button } from "antd";
import { ReactComponent as Back } from "../../../../assets/svgs/back-button.svg";
import { getApi } from "../../../../api/api_instance";
import { API_URLS } from "../../../../api/apiUrls";
import { SearchBar } from "../../../../components/Elements/Search/SearchBar";
import SectionList from "./components/SectionList";
import SubsectionList from "./components/SubSectionList";
import QuestionList from "./components/QuestionList";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { ArrowUpOutlined } from "@ant-design/icons";

const ViewForm = () => {
  const { formId } = useParams();
  const location = useLocation();
  const {
    standardFormAllotmentId: stateAllotmentId,
    user_id: stateUserId,
    projectId,
    templateId,
    employeeId,
    userId,
    userSubDocId,
    allotmentId,
  } = location.state || {};

  const resolvedAllotmentId = stateAllotmentId || formId;
  const resolvedUserId = stateUserId || userId;

  // Resolve IDs for API calls
  const getId = (val) => (typeof val === "object" ? val?._id : val);
  const resolvedProjectId = getId(projectId);
  const resolvedTemplateId = getId(templateId);
  const resolvedEmployeeId = getId(employeeId) || getId(userId);
  const resolvedSubDocId = getId(allotmentId) || getId(userSubDocId) || formId;

  const sectionurl = (page, pageSize, sort) =>
    API_URLS.GET_CLIENT_ASSIGNED_SECTIONS(
      resolvedProjectId,
      resolvedTemplateId,
      resolvedEmployeeId,
      resolvedSubDocId,
    );
  const subsectionurl = (sectionId) =>
    API_URLS.GET_CLIENT_ASSIGNED_SUBSECTIONS(
      resolvedProjectId,
      resolvedTemplateId,
      resolvedEmployeeId,
      resolvedSubDocId,
      sectionId,
    );
  const questionurl = (subsectionId, search) =>
    API_URLS.GET_GET_STANDARD_FORM_Questions(
      resolvedTemplateId,
      selectedSection,
      subsectionId,
    );

  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("-createdAt");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [flatAnswers, setFlatAnswers] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [expandAll, setExpandAll] = useState(true);
  const [showGoTop, setShowGoTop] = useState(false);
  const pageSize = 20;
  const navigate = useNavigate();

  // Create ref for scrollable container
  const scrollableContainerRef = useRef(null);

  const scrollableContainer = {
    maxHeight: "1000px",
    overflowY: "auto",
    padding: "10px",
    borderRadius: "8px",
  };

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    fetchSections();

    // ✅ Simple window scroll listener
    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ Separate effect for container scroll
  useEffect(() => {
    const handleContainerScroll = () => {
      if (scrollableContainerRef.current) {
        const scrollTop = scrollableContainerRef.current.scrollTop;
        setShowGoTop((prev) => prev || scrollTop > 200);
      }
    };

    const containerElement = scrollableContainerRef.current;
    if (containerElement) {
      containerElement.addEventListener("scroll", handleContainerScroll);
      return () =>
        containerElement.removeEventListener("scroll", handleContainerScroll);
    }
  }, [questions, selectedSubsection]); // Re-attach when content changes

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: sectionurl(currentPage, pageSize, sortOrder),
      });

      if (response.success) {
        setSections(response.data);
        if (response.data.length > 0) {
          const firstSection = response.data[0];
          setSelectedSection(firstSection._id);
          console.log(firstSection);
          fetchSubsections(firstSection._id);
        }
      } else {
        console.error("Failed to fetch sections");
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: subsectionurl(sectionId),
      });
      if (response.success) {
        setSubsections(response.data);
        setSelectedSection(sectionId);

        if (response.data.length > 0) {
          const firstSubsection = response.data[0];
          setSelectedSubsection(firstSubsection._id);
          fetchQuestions(firstSubsection._id);
        }
      } else {
        console.error("Failed to fetch subsections");
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = useCallback(
    async (subsectionId = selectedSubsection) => {
      if (!subsectionId) return;

      setLoading(true);
      try {
        const response = await getApi({
          apiUrl: questionurl(subsectionId, searchTerm),
        });

        if (response.success) {
          console.log("Questions (raw response):", response.data);
          setQuestions(response.data);

          // Fetch answers if we have the necessary IDs
          if (resolvedAllotmentId && subsectionId) {
            try {
              const answerUrl = API_URLS.GET_CLIENT_FORM_ANSWER(
                resolvedAllotmentId,
                selectedSection,
                subsectionId,
                resolvedUserId || "",
              );
              const answerRes = await getApi({ apiUrl: answerUrl });
              if (answerRes.success && answerRes.data) {
                console.log("ViewForm - API Response Data:", answerRes.data);
                const combinedMap = {};

                const flattenAnswers = (answersList, prefix = "") => {
                  if (!answersList || !Array.isArray(answersList)) return;

                  answersList.forEach((ans) => {
                    const bId = ans.questionId || ans.questionID;
                    if (!bId) return;

                    let mId = bId;
                    // Support for prefixed keys if needed (from UserAssignFormDetail logic)
                    if (prefix) {
                      if (bId.includes(":::")) {
                        const [iP, oP] = bId.split(":::");
                        mId = `${iP}${prefix}:::${oP}`;
                      } else {
                        mId = `${bId}${prefix}`;
                      }
                    }

                    if (
                      ans.questionType === "table" ||
                      (ans.tableRows && ans.tableRows.length > 0)
                    ) {
                      const tableData = { default: ans.tableRows || [] };
                      combinedMap[mId] = tableData;

                      // Fallback for ID mismatch between capital and non-capital goods tables
                      if (mId === "scope3_categories_capital_goods_table") {
                        combinedMap[
                          "scope3_categories_non_capital_goods_table"
                        ] = tableData;
                      } else if (
                        mId === "scope3_categories_non_capital_goods_table"
                      ) {
                        combinedMap["scope3_categories_capital_goods_table"] =
                          tableData;
                      }
                      console.log(
                        `ViewForm - Flattened Table [${mId}]:`,
                        tableData,
                      );
                    } else {
                      const newVal =
                        ans.value !== undefined ? ans.value : ans.answer;
                      if (newVal !== undefined) {
                        const currentVal = combinedMap[mId];
                        if (currentVal === undefined) {
                          combinedMap[mId] = newVal;
                        } else {
                          const currentArray = Array.isArray(currentVal)
                            ? currentVal
                            : [currentVal];
                          const newArray = Array.isArray(newVal)
                            ? newVal
                            : [newVal];
                          combinedMap[mId] = Array.from(
                            new Set([...currentArray, ...newArray]),
                          );
                        }
                        console.log(
                          `ViewForm - Flattened [${mId}]:`,
                          combinedMap[mId],
                        );
                      }
                    }
                  });
                };

                answerRes.data.forEach((item) => {
                  if (item.answers && Array.isArray(item.answers)) {
                    flattenAnswers(item.answers);
                  }
                });

                setFlatAnswers(combinedMap);
              }
            } catch (err) {
              console.error("Error fetching answers:", err);
            }
          }
        } else {
          setQuestions([]);
        }
      } catch (error) {
        console.error("Error fetching questions", error);
        message.error("Failed to fetch questions");
      } finally {
        setLoading(false);
      }
    },
    [searchTerm],
  );

  const scrollToTop = () => {
    // First scroll the container to top
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
    // Then scroll window to top
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Hide the button immediately
    setShowGoTop(false);
  };

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return (
    <div className=" md:p-6 lg:p-0 bg-[#F5F5F5] mb-20 sm:mb-6 md:mb-6">
      {/* Header */}
      <div className="flex items-center gap-2 sm:mb-4 mb-2 text-lg sm:text-xl font-bold flex-wrap">
        <IoArrowBackCircleOutline
          onClick={() => navigate(-1)}
          className="w-7 h-7 sm:w-9 sm:h-9 text-[#06513D] hover:text-white hover:bg-[#06513D] rounded-full transition cursor-pointer"
        />
        <div className="text-[#06513D] font-[Calibri] text-[18px] sm:text-[20px] font-bold">
          ALL QUESTIONS
        </div>
      </div>

      {/* Section & Subsection */}
      <div className="w-full bg-white border border-[#3BA2B959] rounded-[10px]  shadow-md mb-6">
        <SectionList
          sections={sections}
          selectedSection={selectedSection}
          onSectionSelect={fetchSubsections}
        />

        <hr className="border-t border-[#3BA2B959] shadow-sm" />

        {selectedSection && (
          <SubsectionList
            subsections={subsections}
            selectedSubsection={selectedSubsection}
            onSubsectionSelect={(id) => {
              setSelectedSubsection(id);
              fetchQuestions(id);
            }}
          />
        )}
      </div>

      {/* Questions */}
      <div className="w-full bg-white border border-[#3BA2B959] rounded-[10px] pt-5 shadow-md mb-6 relative">
        {/* <div className="px-4 sm:px-6">
        <div className="flex flex-wrap gap-4 items-center mb-6">
          <div className="bg-[#B6D4D9] bg-opacity-20 h-10 px-4 border border-[#06513D] rounded-md text-[#06513D] text-base font-light flex items-center">
            <p className="text-[16px] font-[Calibri] leading-[1] h-full flex items-center">
              TOTAL QUESTION
            </p>
          </div>

          <SearchBar
            width="100%"
            className="max-w-[300px] w-full"
            onSearch={(value) => setSearchTerm(value)}
          />
        </div>
      </div> */}

        <div className="mt-4 ml-3">
          <div className="flex items-center w-[90%] m-auto">
            <div className="flex gap-2">
              <p className="h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-base font-light px-6">
                TOTAL QUESTION
              </p>

              {/* Search Bar */}

              <SearchBar
                width="300px"
                onSearch={(value) => setSearchTerm(value)}
              />
            </div>
            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-[13px] font-light px-8 py-6 ml-44 mb-5"
              onClick={() => setExpandAll((prev) => !prev)}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>
        </div>

        <div
          style={scrollableContainer}
          ref={scrollableContainerRef}
          className="overflow-y-auto max-h-[60vh] px-4 sm:px-6"
        >
          <QuestionList
            loading={loading}
            questions={questions}
            selectedSubsection={selectedSubsection}
            expandAll={expandAll}
            answers={flatAnswers}
          />
          {showGoTop && questions.length > 0 && (
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowUpOutlined />}
              size="large"
              onClick={scrollToTop}
              style={{
                position: "absolute",
                bottom: "30px",
                right: "60px",
                background: "#06513D",
                zIndex: 1000,
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewForm;
